import React, {
  memo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';

import Close from 'shared-vectors/icons/DsClose';
import { Icon } from 'shared-vectors';

import getMapCardCoords from 'client/searchMap/helpers/getMapCardCoords';

import CardsReel from './CardsReel';
import Skeleton from './Skeleton';

import './style.scss';

const PositionContainer = ({ coords, children }) => {
  if (coords && coords.movedToTop) {
    return (
      <div style={{ width: '100%', position: 'absolute', bottom: 0, left: 0 }}>
        {children}
      </div>
    );
  }
  return children;
};

const MapCard = ({
  device,
  activeMarkerData,
  fetchInfoboxData,
  viewport,
  setActiveMarker,
  setCurrentFloorplan,
  loadDetail,
}) => {
  const {
    markerWidth,
    position,
    map,
    listings,
    unSetActiveMarker,
    close,
  } = activeMarkerData;

  const mulitpleAds = (listings || []).length > 1;
  const listingsString = (listings || []).map(l => l.id).join(',');

  const container = useRef(null);

  const [active, setActive] = useState(false);
  const [data, setData] = useState(null);
  const [coords, setCoords] = useState(null);
  const [transform, setTransform] = useState(null);

  const closeCard = () => {
    const delay = ['desktop', 'tablet'].includes(device) ? 0 : 205;
    setTransform(null);
    setTimeout(() => {
      setData(null);
      setCoords(null);
      setActiveMarker({ uKey: null, activeMarkerData: null });
      unSetActiveMarker && unSetActiveMarker();
    }, delay);
  };

  const handleKeydown = useCallback((evt) => {    
    if (evt.keyCode === 27) {      
      closeCard();
    }
  }, [data]);

  useEffect(() => {
    if (data && device === 'desktop') {
      window.addEventListener('keydown', handleKeydown);
    } else {
      window.removeEventListener('keydown', handleKeydown);
    }
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [data, device, handleKeydown]);

  useEffect(() => {
    if (listingsString) {
      const cardCoordinates = ['desktop', 'tablet'].includes(device)
        ? getMapCardCoords(
          {
            mrkX: position.x,
            mrkY: position.y,
            mapW: map.w,
            mapH: map.h,
            cardContainerH: mulitpleAds ? 484 : 412,
            markerWidth,
          },
        ) : {
          x: position.x,
          y: position.y,
        };
      setActive(true);
      setCoords(cardCoordinates);
      if (!['desktop', 'tablet'].includes(device)) {
        setTimeout(() => setTransform('translate3d(-50%, 0, 0)'), 75);
      }         
      const fetchData = async () => {
        const res = await fetchInfoboxData({ listingIds: listings });
        setData(res.body.list);
      };
      setData(null);
      fetchData();
    }    
  }, [listingsString, device]);

  useEffect(() => {
    if (close) {      
      const delay = ['desktop', 'tablet'].includes(device) ? 0 : 205;
      setTransform(null);
      setTimeout(() => {
        setData(null);
        setCoords(null);
        setActiveMarker({ uKey: null, activeMarkerData: null });
        unSetActiveMarker && unSetActiveMarker();
      }, delay);
    }
  }, [close, unSetActiveMarker, device]);

  // useEffect(() => {
  //   if (data) {
  //     console.log(container.current.clientHeight);
  //     const cardCoordinates = ['desktop', 'tablet'].includes(device)
  //       ? getMapCardCoords(
  //         {
  //           mrkX: position.x,
  //           mrkY: position.y,
  //           mapW: map.w,
  //           mapH: map.h,
  //           cardContainerH: container.current.clientHeight,
  //         },
  //       ) : {
  //         x: position.x,
  //         y: position.y,
  //       };
  //     setCoords(cardCoordinates);
  //     // if (!['desktop', 'tablet'].includes(device)) {
  //     //   setTimeout(() => setTransform('translate3d(-50%, 0, 0)'), 75);
  //     // }
  //   }
  // }, [data, device]);

  const closeClick = () => setActiveMarker({
    uKey: null,
    activeMarkerData: {
      close: true,
      unSetActiveMarker,
    },
  });

  if (active) {
    const hasSlider = mulitpleAds && ['desktop', 'tablet'].includes(device) ? 1 : 0;
    const hasCounter = mulitpleAds && !['desktop', 'tablet'].includes(device) ? 1 : 0;
    let h = null;
    if (['desktop', 'tablet'].includes(device)) {
      h = mulitpleAds ? 484 : 412;
    }
    return (
      <div
        ref={container}
        className="csa_mapsearch__card"
        style={coords ? {
          [['desktop', 'tablet'].includes(device) ? 'top' : 'bottom']: coords.y || 0,
          left: coords.x || 0,
          transform,
          height: h,
        } : null}
      >
        {data ? (
          <PositionContainer coords={coords}>
            <div
              className={`csa_mapsearch__card__close${hasCounter ? ' wCounter pt--s pb--s pl--ms pr--m' : ' p--s'}${hasSlider ? ' wArrows' : ''} c-bg--w c-txt--secondary b-r--pill b--s is-clickable`}
              onClick={closeClick}
            >
              <Icon name={Close} width="16px" />          
              {hasCounter ? <p className="pl--xs tp-s--m tp-w--m">{(listings || []).length} a questo indirizzo</p> : null}
            </div>          
            <CardsReel
              data={data}
              viewport={viewport}
              isDesktop={['desktop', 'tablet'].includes(device)}
              hasSlider={hasSlider}
              setCurrentFloorplan={setCurrentFloorplan}
              loadDetail={loadDetail}
            />
          </PositionContainer>
        ) : (
          <Skeleton 
            hasSlider={hasSlider}
            device={device}
          />
        )}
      </div>
    );
  } 

  return null;
};

export default memo(MapCard);

MapCard.propTypes = {
  device: PropTypes.string,
  activeMarkerData: PropTypes.instanceOf(Object),
  fetchInfoboxData: PropTypes.func,
  viewport: PropTypes.instanceOf(Object),
  setActiveMarker: PropTypes.func,
  setCurrentFloorplan: PropTypes.func,
  loadDetail: PropTypes.func,
};

MapCard.defaultProps = {
  device: '',
  activeMarkerData: {},
  fetchInfoboxData: () => {},
  viewport: {},
  setActiveMarker: () => {},
  setCurrentFloorplan: () => {},
  loadDetail: () => {},
};
